import { useCallback, useEffect, useRef, useState } from 'react'

import { SmepEvent, SmepEventConfig } from '@nativewaves/nw-cloud-sdk/smep'
import { useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useEventUpdateMutation } from 'hooks/mutations/smep'
import { eventQueryKeys } from 'services/queryKeys'

import { newId } from './ids'
import { parseConfig, serializeConfig } from './model'
import type { ConfigFormValues } from './types'

/** SDK sentinel: patching `manualEndTime` with this clears the end time (open end). */
const CLEAR_TIME = '0001-01-01T00:00:00.000Z'

export type UseConfigFormProps = {
  event: SmepEvent
  config: SmepEventConfig
  /** Called after the config was successfully persisted (e.g. to collapse an inline editor). */
  onSaved?: () => void
  /** Also edit + persist the event schedule (`manualStartTime`/`manualEndTime`) alongside the config. */
  includeSchedule?: boolean
}

const scheduleDefaults = (event: SmepEvent): Pick<ConfigFormValues, 'manualStartTime' | 'manualEndTime'> => ({
  manualStartTime: event.startTime,
  manualEndTime: event.endTime ?? null,
})

export const useConfigForm = ({ event, config, onSaved, includeSchedule }: UseConfigFormProps) => {
  const { t } = useTranslation(['domain'])
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const eventId = event.id

  const formMethods = useForm<ConfigFormValues>({
    defaultValues: { ...parseConfig(config), ...(includeSchedule ? scheduleDefaults(event) : {}) },
  })
  const { mutateAsync, isPending } = useEventUpdateMutation()

  const [externalChange, setExternalChange] = useState(false)
  const lastConfigRef = useRef(config)

  // Adopt server truth when the underlying config changes (e.g. saved from the
  // JSON tab). Never silently discard in-progress edits — surface a banner instead.
  useEffect(() => {
    const next = event.config
    if (!next || next === lastConfigRef.current) {
      return
    }
    lastConfigRef.current = next
    if (formMethods.formState.isDirty) {
      setExternalChange(true)
    } else {
      formMethods.reset({ ...parseConfig(next), ...(includeSchedule ? scheduleDefaults(event) : {}) })
    }
  }, [event, event.config, formMethods, includeSchedule])

  const reloadFromServer = useCallback(() => {
    if (event.config) {
      formMethods.reset({ ...parseConfig(event.config), ...(includeSchedule ? scheduleDefaults(event) : {}) })
    }
    setExternalChange(false)
  }, [event, formMethods, includeSchedule])

  const handleSubmit = formMethods.handleSubmit(async (values) => {
    // Merge into the freshest server config so a concurrent JSON-tab change to an
    // unsupported field is preserved (no clobber).
    const fresh = await queryClient.ensureQueryData(eventQueryKeys.detail({ eventId }))
    const payload = serializeConfig(fresh.config ?? config, values, newId)

    const updated = await mutateAsync({
      eventId,
      smepEventEdit: {
        config: payload,
        ...(includeSchedule && {
          manualStartTime: values.manualStartTime,
          manualEndTime: values.manualEndTime ?? CLEAR_TIME,
        }),
      },
    })

    if (updated.config) {
      lastConfigRef.current = updated.config
      formMethods.reset(parseConfig(updated.config), { keepSubmitCount: true })
    }
    setExternalChange(false)
    enqueueSnackbar(t('domain:Workspace.Event.ConfigEditor.saved', { name: updated.name }), { variant: 'success' })
    onSaved?.()
  })

  return { formMethods, handleSubmit, isSaving: isPending, externalChange, reloadFromServer }
}
